import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { RecordPeek, PeekLink, type PeekTarget } from '../components/RecordPeek';
import { DesignStatusBadge, DesignStatusSelect } from '../components/DesignStatus';
import { fetchAllQueue, formatJc } from './Dashboard';
import { ClientSummary, type ClientRow } from '../components/ClientSummary';
import { useAuth, can, ROLE } from '../lib/auth';
import { calculateDueDate } from '../lib/tat';
import { useOpenSlaInstance } from '../lib/useOpenSlaInstance';
import type { TatSettings } from './TatSettings';

export type QueueKind = 'crm' | 'design' | 'purchase' | 'jobcard' | 'dispatch';

const PAGE_SIZE = 50;

interface Row {
  poId: string; poNumber: string | null; poDate: string | null;
  requestedDate: string | null; qty: string | null; jcQty: number | null;
  /** The latest job card's date (yyyy-mm-dd); null before one is raised. */
  jcDate?: string | null;
  /** 1: the latest job card is the balance of a part order (an earlier card went out) and has no DOD of its own yet. */
  needsBatchDod?: number | boolean | null;
  orderType: string | null;
  jobName: string | null; client: string | null;
  productId: string | null; productName: string | null;
  /** The product's A/W Material No (entered on the PO or the product). */
  awMaterialNo?: string | null;
  cutL: string | null; cutW: string | null;
  fullL: string | null; fullW: string | null;
  board: string | null; gsm: string | null;
  promiseDate: string | null; jcPromiseDate: string | null; oldPromiseDate: string | null; revisionReason: string | null;
  crmUserId: number | null; crmName: string | null;
  designStatus: string | null; designDoneBy: number | null; designDoneByName: string | null; designUpdatedOn: string | null;
  designMessage: string | null; designTatOverride: string | null;
  // The designer's status on the order (shown to the CRM).
  clientPendingReason?: string | null; clientPendingOn?: string | null; clientPendingByName?: string | null;
  clientWait?: boolean | number | null;
  artworkNo?: string | null;
  boardStatus: string | null; boardExpectedDate: string | null; boardMarkedBy: number | null;
  isCancelled?: boolean;
  topFinish: string | null; partyGsm: string | null;
  // Board spec ids and who filled it (Product Master).
  paperTypeId?: string | null; topFinishId?: string | null;
  boardSpecByName?: string | null; boardSpecOn?: string | null; boardSpecPo?: string | null;
  jobId: string | null; dispatchId: string | null; linkedPoCount?: number | null; sheetsWithWastage?: string | null;
  /** All the POs on this row's job card, together (a clubbed card). */
  linkedPoQty?: number | null;
  /** The board was marked on this PO before its job card was made. */
  boardBeforeJc?: number | null; boardMarkedOn?: string | null;
  dispatchDate?: string | null; invoiceNo?: string | null;
  // FG fields
  totalBoxes?: number | null; producedQty?: number | null;
  dispatchedQty?: number | null; scrappedQty?: number | null;
  dispatchApproved?: number | null; pendingRequest?: string | null;
  jobStatus?: string | null;
  clientSchedulePending?: string | number | boolean | null;
  /** Dispatch queue: false while dispatch goes by typed quantity alone. */
  dispatchGates?: boolean;
  /** 1: a Change / Repeat order that needs Quality's master file. */
  needsMasterFile?: number | null;
  /** Office time 'YYYY-MM-DD HH:mm' the master file was handed over (or marked not found). */
  masterFileOn?: string | null;
  masterFileNotFound?: number | null;
}

/**
 * What is left to send.  Gates on: produced (QC-approved) less sent and
 * scrapped, null before any production.  Gates off: the job card quantity
 * less sent and scrapped, null when the card has no quantity.
 */
function dispatchRemaining(r: Row): number | null {
  const sent = (Number(r.dispatchedQty) || 0) + (Number(r.scrappedQty) || 0);
  if (r.dispatchGates === false) return r.jcQty != null && Number(r.jcQty) > 0 ? Number(r.jcQty) - sent : null;
  const produced = Number(r.producedQty) || 0;
  return produced > 0 ? produced - sent : null;
}

const TITLES: Record<QueueKind, { title: string; blurb: string }> = {
  crm:      { title: 'CRM — set delivery date',  blurb: 'Orders with no DOD yet. The customer’s requested date is shown for reference; the date you commit is recorded separately.' },
  design:   { title: 'Design queue',             blurb: 'New and Change orders whose design information is not yet complete.' },
  purchase: { title: 'Purchase — board',         blurb: 'Cut size, GSM and board for orders ready to procure. Mark the board available, or give the date it will be.' },
  jobcard:  { title: 'Raise job card',           blurb: 'Orders with no job card yet. DOD and board status are shown so you can see what is ready — nothing is blocked, you decide.' },
  dispatch: { title: 'Dispatch',                 blurb: 'Jobs ready to go. Record each dispatch with its quantity — invoice, packages, vehicle and driver are optional. A job leaves this list once everything it made is dispatched (one with no production entries, on its first dispatch).' },
};

/** The CRM page's cards, in the order an order moves through them. */
const CRM_STAGES = [
  { key: 'pending', label: 'Set delivery date', hint: 'No DOD committed yet', urgent: true },
  { key: 'promised', label: 'DOD set', hint: 'In production — red rows are late', urgent: false },
  { key: 'fg', label: 'Ready to dispatch', hint: 'Approve dispatch, scrap or reissue', urgent: false },
  { key: 'dispatched', label: 'Dispatched', hint: 'Recently sent', urgent: false },
] as const;

function fmtDate(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
/** "28 Sept, 14:05" */
function fmtDateTime(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
/** Whole mm — operators read this at a glance. */
function mm(v: string | null): string {
  const n = Number(v);
  return Number.isFinite(n) && v != null && v !== '' ? String(Math.round(n)) : '';
}

function formatInches(l: string | null, w: string | null): string {
  const ln = Number(l);
  const wn = Number(w);
  if (!Number.isFinite(ln) || !Number.isFinite(wn) || !l || !w) return '—';
  if (ln >= 150 || wn >= 150) {
    const li = (ln / 25.4).toFixed(1).replace(/\.0$/, '');
    const wi = (wn / 25.4).toFixed(1).replace(/\.0$/, '');
    return `${li}" x ${wi}"`;
  }
  return '';
}



export function FlowQueuePage({ kind }: { kind: QueueKind }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sub, setSub] = useState<'pending' | 'promised' | 'revised' | 'dispatched' | 'fg' | 'expected' | 'available' | 'unclaimed' | 'mine' | 'all' | 'design_approvals' | 'client_pending' | 'history'>('pending');
  const [dateColumn, setDateColumn] = useState<'dod' | 'po'>('dod');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [page, setPage] = useState(1);
  // Which record the detail popup is showing, if any.
  const [peek, setPeek] = useState<PeekTarget | null>(null);
  /** CRM: the job cards-by-client report, open or not. */
  const [byClient, setByClient] = useState(false);

  // A Process Coordinator (or VPPL) sees every CRM's orders and can narrow to
  // one. A CRM holds no such permission and is pinned to their own accounts by
  // the API, so this control simply does not render for them.
  const { user } = useAuth();
  const seesAllCrms = can(user, 'VIEW_ALL_CRMS');
  const [crmFilter, setCrmFilter] = useState('');
  const crms = useQuery({
    enabled: seesAllCrms,
    queryKey: ['flow-crms'],
    queryFn: () => api<{ id: number; name: string }[]>('/api/flow/crms'),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    // The design team shares the work: every designer sees and can finish
    // every design order ('all'), as Super Admin does.
    // Artwork approval and waiting on the client now show in each order's
    // "Where it is" (an old push link to those tabs lands on the first card).
    setSub(kind === 'design'
      ? (user?.rollId === ROLE.SuperAdmin || user?.rollId === ROLE.Designers ? 'all' : 'mine')
      : 'pending');
    setDateColumn('dod');
    setDateFrom('');
    setDateTo('');
    setTypeFilter('All');
    setPage(1);
    setQ('');
    setSearch('');
    setCrmFilter('');
    setError(null);
  }, [kind]);

  // Search as you type, once typing pauses.
  useEffect(() => {
    if (q.trim() === search) return;
    const t = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [q, search]);

  const list = useQuery({
    queryKey: ['flow-queue', kind, sub, search, page, crmFilter, dateColumn, dateFrom, dateTo, typeFilter],
    queryFn: async () => {
      const sp = new URLSearchParams({ kind, page: String(page), pageSize: String(PAGE_SIZE) });
      if (search) sp.set('q', search);
      if (sub && (kind === 'crm' || kind === 'purchase' || kind === 'design')) sp.set('sub', sub);
      if (dateFrom) sp.set('dateFrom', dateFrom);
      if (dateTo) sp.set('dateTo', dateTo);
      if (dateFrom || dateTo) sp.set('dateColumn', dateColumn);
      if (typeFilter && typeFilter !== 'All') sp.set('typeFilter', typeFilter);
      if (seesAllCrms && crmFilter) sp.set('crmUserId', crmFilter);
      const res = await api<{ rows: Row[]; total: number; page: number; pageSize: number; dispatchGates?: boolean }>(
        `/api/flow/queue?${sp.toString()}`,
      );
      if (res.dispatchGates === false) for (const r of res.rows) r.dispatchGates = false;
      return res;
    },
    placeholderData: (prev, prevQuery) => {
      const prevKey = prevQuery?.queryKey as string[] | undefined;
      if (!prevKey) return undefined;
      if (prevKey[2] !== sub) return undefined;
      return prev;
    },
    staleTime: 30_000,
  });

  // How many orders wait in each CRM card, under the same filters.  Keyed
  // under 'flow-queue' so it refreshes whenever the list does.
  const counts = useQuery({
    enabled: kind === 'crm',
    queryKey: ['flow-queue', 'counts', search, crmFilter, dateColumn, dateFrom, dateTo, typeFilter],
    queryFn: () => {
      const sp = new URLSearchParams({ kind: 'crm' });
      if (search) sp.set('q', search);
      if (dateFrom) sp.set('dateFrom', dateFrom);
      if (dateTo) sp.set('dateTo', dateTo);
      if (dateFrom || dateTo) sp.set('dateColumn', dateColumn);
      if (typeFilter && typeFilter !== 'All') sp.set('typeFilter', typeFilter);
      if (seesAllCrms && crmFilter) sp.set('crmUserId', crmFilter);
      return api<Record<string, number>>(`/api/flow/queue-counts?${sp.toString()}`);
    },
    staleTime: 30_000,
  });

  const act = useMutation({
    mutationFn: (v: { path: string; body: unknown }) =>
      api(`/api/flow/${v.path}`, { method: 'POST', body: JSON.stringify(v.body) }),
    onSuccess: async () => { setError(null); await qc.refetchQueries({ queryKey: ['flow-queue'] }); },
    onError: (e) => setError(friendly((e as Error).message)),
    onSettled: () => setBusy(null),
  });

  function run(id: string, path: string, body: unknown) {
    setBusy(id); setError(null);
    act.mutate({ path, body });
  }

  // Whether this user may DO the thing this queue is for, as opposed to read
  // it. The Process Coordinator reads every queue and acts on none, so the
  // action column collapses to "view only" rather than showing buttons that
  // would come back 403.
  const ACTION_PERM = {
    crm: 'SET_PROMISE_DATE',
    design: 'MARK_DESIGN_DONE',
    purchase: 'MARK_BOARD_STATUS',
    dispatch: 'RECORD_DISPATCH',
    jobcard: 'CREATE_JOB',
  } as const;
  const canAct = can(user, ACTION_PERM[kind]);

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const meta = TITLES[kind];

  // Where each CRM order is on the floor - design to dispatch - for the
  // rows on this page.  Refreshes with the list.
  const poIds = kind === 'crm' ? rows.map((r) => r.poId).join(',') : '';
  const progress = useQuery({
    enabled: !!poIds,
    queryKey: ['flow-queue', 'progress', poIds],
    queryFn: () => api<{ rows: PoProgress[] }>(`/api/flow/po-progress?pos=${encodeURIComponent(poIds)}`),
    staleTime: 30_000,
  });
  const progressOf = new Map((progress.data?.rows ?? []).map((p) => [p.poId, p]));

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-semibold text-brand">{kind === 'crm' ? 'Delivery dates' : <span className="capitalize">{meta.title}</span>}</h1>
          {kind !== 'crm' && <p className="text-sm text-slate-500 max-w-2xl">
            {kind === 'design'
              ? <>The design team's New and Change orders whose design is not yet complete — anyone can work on any of them. Orders that came straight to design are in the <Link className="underline text-brand" to="/artworks">Artwork Register</Link>.</>
              : meta.blurb}
          </p>}
        </div>
      </div>

      {kind === 'purchase' && (
        <div className="flex flex-wrap gap-2 mb-4 bg-slate-100 p-1 rounded-md w-fit">
          <button
            onClick={() => { setPage(1); setSub('pending'); }}
            className={`px-3 py-1.5 text-xs font-semibold rounded ${(!sub || sub === 'pending') ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Pending Boards
          </button>
          <button
            onClick={() => { setPage(1); setSub('expected'); }}
            className={`px-3 py-1.5 text-xs font-semibold rounded ${sub === 'expected' ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Expected Boards
          </button>
          <button
            onClick={() => { setPage(1); setSub('available'); }}
            className={`px-3 py-1.5 text-xs font-semibold rounded ${sub === 'available' ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Available Boards
          </button>
        </div>
      )}

      {kind === 'crm' && (() => {
        // Stage cards, then one row of filters.  Every filter applies as soon
        // as it changes (search after a short pause) - no Filter button.
        const filtered = !!(q || crmFilter || typeFilter !== 'All' || dateFrom || dateTo);
        return (
          <div className="mb-3 space-y-3">
            {/* The order's journey, left to right: each card says what is
                waiting there and how many.  Click one to work it. */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
              {CRM_STAGES.map((st) => {
                const active = sub === st.key || (st.key === 'promised' && sub === 'revised');
                const n = counts.data?.[st.key];
                return (
                  <button key={st.key} onClick={() => { setPage(1); setSub(st.key); }}
                          className={`rounded-lg border p-3 text-left transition ${active ? 'border-brand bg-brand text-white shadow' : 'border-slate-200 bg-white hover:border-brand hover:shadow-sm'}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`text-sm font-semibold ${active ? 'text-white' : 'text-slate-800'}`}>{st.label}</span>
                      {st.key !== 'dispatched' && (
                        <span className={`text-xl font-bold tabular-nums ${active ? 'text-white' : n ? (st.urgent ? 'text-red-600' : 'text-brand') : 'text-slate-300'}`}>
                          {n ?? '·'}
                        </span>
                      )}
                    </div>
                    <div className={`mt-0.5 text-xs ${active ? 'text-white/80' : 'text-slate-500'}`}>{st.hint}</div>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end">
              <button onClick={() => setByClient((v) => !v)}
                      className={`rounded-md border px-3 py-1.5 text-sm font-medium ${byClient ? 'border-brand bg-brand text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-brand'}`}>
                {byClient ? 'Hide job cards by client' : 'Job cards by client'}
              </button>
            </div>
            {byClient && <ClientReport crmUserId={seesAllCrms ? crmFilter : ''} onOpenPo={(id) => setPeek({ kind: 'po', id })} />}
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
              <div className="relative w-full sm:w-72">
                <input type="search" placeholder="Search PO, JC, client, product…" value={q}
                       onChange={(e) => setQ(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); setSearch(q); } }}
                       className="input py-1.5 pl-8" />
                <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 3.3 9.9l3.15 3.15a.75.75 0 1 0 1.06-1.06l-3.15-3.15A5.5 5.5 0 0 0 9 3.5ZM5 9a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z" clipRule="evenodd" /></svg>
              </div>
              {seesAllCrms && crms.data && (
                <select className="input !w-auto py-1.5" value={crmFilter} onChange={(e) => { setCrmFilter(e.target.value); setPage(1); }} title="CRM">
                  <option value="">All CRMs</option>
                  <option value="unassigned">Unassigned</option>
                  {crms.data.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              )}
              <select className="input !w-auto py-1.5" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} title="Order type">
                <option value="All">All types</option>
                <option value="New">New</option>
                <option value="Change">Change</option>
                <option value="Repeat">Repeat</option>
              </select>
              <div className="flex w-full items-center gap-1 rounded-md border border-slate-300 bg-white pl-1 shadow-sm sm:w-auto">
                <select className="w-[5.75rem] shrink-0 rounded border-0 bg-transparent py-1.5 pl-1 pr-6 text-sm text-slate-600 focus:ring-0" value={dateColumn}
                        onChange={(e) => { setDateColumn(e.target.value as 'dod' | 'po'); setPage(1); }} title="Which date to filter on">
                  <option value="dod">DOD</option>
                  <option value="po">PO date</option>
                </select>
                <input type="date" aria-label="From" className="min-w-0 flex-1 sm:w-[8.5rem] sm:flex-none border-0 bg-transparent px-1 py-1.5 text-sm focus:ring-0" value={dateFrom}
                       onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
                <span className="text-slate-400">→</span>
                <input type="date" aria-label="To" className="min-w-0 flex-1 sm:w-[8.5rem] sm:flex-none border-0 bg-transparent px-1 py-1.5 text-sm focus:ring-0" value={dateTo}
                       onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
              </div>
              {filtered && (
                <button className="text-sm text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline"
                        onClick={() => { setQ(''); setSearch(''); setCrmFilter(''); setTypeFilter('All'); setDateFrom(''); setDateTo(''); setPage(1); }}>
                  Clear filters
                </button>
              )}
            </div>
          </div>
          </div>
        );
      })()}

      {kind === 'design' && (
        <div className="flex flex-wrap gap-2 mb-4 bg-slate-100 p-1 rounded-md w-fit">
          <button
            onClick={() => { setPage(1); setSub(user?.rollId === ROLE.SuperAdmin || user?.rollId === ROLE.Designers ? 'all' : 'mine'); }}
            className={`px-3 py-1.5 text-xs font-semibold rounded ${sub !== 'history' && sub !== 'unclaimed' ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Active
          </button>
          {/* New / Change orders nobody has claimed yet - claim one from here. */}
          <button
            onClick={() => { setPage(1); setSub('unclaimed'); }}
            className={`px-3 py-1.5 text-xs font-semibold rounded ${sub === 'unclaimed' ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Unclaimed
          </button>
          <button
            onClick={() => { setPage(1); setSub('history'); }}
            className={`px-3 py-1.5 text-xs font-semibold rounded ${sub === 'history' ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            History
          </button>
        </div>
      )}

      {kind !== 'crm' && (
        <form className="flex flex-wrap items-center gap-2 mb-4" onSubmit={(e) => { e.preventDefault(); setSearch(q); setPage(1); }}>
          <input className="input max-w-sm" placeholder="PO number, client, product…"
                 value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn-primary" type="submit">Search</button>
          {search && <button type="button" className="btn-ghost" onClick={() => { setQ(''); setSearch(''); setPage(1); }}>Reset</button>}

          {seesAllCrms && (
            <label className="ml-auto flex items-center gap-2 text-sm text-slate-600">
              CRM
              <select className="input py-1 w-48" value={crmFilter}
                      onChange={(e) => { setCrmFilter(e.target.value); setPage(1); }}>
                <option value="">All CRMs</option>
                {crms.data?.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
                {/* Orders nobody owns yet are the ones most likely to go quiet,
                    so they get their own selection rather than hiding in "All". */}
                <option value="unassigned">— Unassigned —</option>
              </select>
            </label>
          )}
        </form>
      )}

      {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

      <div className="card overflow-hidden relative">
        <div className="card-header flex justify-between">
          <span className="capitalize">{kind === 'crm' ? CRM_STAGES.find((st) => st.key === sub || (st.key === 'promised' && sub === 'revised'))?.label ?? sub : meta.title}</span>
          <span className="text-xs font-normal">
            {list.isFetching && !list.isPlaceholderData && <span className="text-brand mr-2">Loading…</span>}
            {total} order{total === 1 ? '' : 's'}
            {totalPages > 1 && ` · page ${page} of ${totalPages}`}
          </span>
        </div>
        {list.isFetching && !list.isPlaceholderData && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
            <div className="animate-spin h-6 w-6 border-2 border-brand border-t-transparent rounded-full" />
          </div>
        )}
        {/* Scrolls inside the card so the column headings stay in view. */}
        <div key={`${sub}-${page}`} className="max-h-[calc(100vh-14rem)] min-h-[16rem] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-[5] bg-slate-100 text-left text-xs uppercase text-slate-600 shadow-[0_1px_0_#e2e8f0]">
              <tr>
                {kind !== 'purchase' && <th className="px-3 py-2">PO</th>}
                <th className="px-3 py-2">Client</th>
                {/* Only meaningful when looking across CRMs — a CRM's own queue
                    would repeat their own name on every row.  Designers see the
                    whole board, so they get the column too — telling them
                    which CRM owns the PO saves a lookup. */}
                {(seesAllCrms || kind === 'design') && <th className="px-3 py-2">CRM</th>}
                <th className="px-3 py-2">Product</th>
                {kind === 'design' && <th className="px-3 py-2">A/W Material No</th>}
                <th className="px-3 py-2">JC</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2 text-right">{kind === 'crm' ? 'JC Qty' : 'Qty'}</th>
                {kind === 'purchase' && <th className="px-3 py-2 text-right">Sheet+W</th>}
                {kind === 'crm' && (
                  <>
                    <th className="px-3 py-2" title="The job card's date; the PO's (grey) until one is raised">JC date</th>
                    <th className="px-3 py-2">Age</th>
                    {(sub === 'promised' || sub === 'revised' || sub === 'dispatched' || sub === 'fg') && <th className="px-3 py-2">Current DOD</th>}
                    {(sub === 'promised' || sub === 'revised') && <><th className="px-3 py-2">Old DOD</th><th className="px-3 py-2">Revision Reason</th></>}
                    {sub === 'dispatched' && <><th className="px-3 py-2">Invoice No</th><th className="px-3 py-2">Dispatch Date</th></>}
                    {sub === 'fg' && <>
                      <th className="px-3 py-2 text-right">PO Qty</th>
                      <th className="px-3 py-2 text-right">Produced</th>
                      <th className="px-3 py-2 text-right">Dispatched</th>
                      <th className="px-3 py-2 text-right">Scrapped</th>
                      <th className="px-3 py-2 text-right">Remaining</th>
                      <th className="px-3 py-2">Status</th>
                    </>}
                    {sub !== 'fg' && <th className="px-3 py-2">Board</th>}
                    <th className="px-3 py-2" title="Design, job card, board cut, printing plan, printed, production, dispatch">Where it is</th>
                  </>
                )}
                {kind === 'purchase' && (
                  <>
                    <th className="px-3 py-2">DOD</th>
                    <th className="px-3 py-2">Cut size</th>
                    <th className="px-3 py-2">Full size</th>
                    <th className="px-3 py-2">GSM</th>
                    <th className="px-3 py-2">Board</th>
                    <th className="px-3 py-2">Party GSM</th>
                    <th className="px-3 py-2">Top Finish</th>
                    <th className="px-3 py-2">Filled by</th>
                    <th className="px-3 py-2">Board TAT</th>
                  </>
                )}
                {kind === 'jobcard' && <><th className="px-3 py-2">DOD</th><th className="px-3 py-2">Board</th></>}
                {/* No separate "Job" header — the JC column above covers it. */}
                {kind === 'dispatch' && <>
                  <th className="px-3 py-2">DOD</th>
                  <th className="px-3 py-2 text-right">Remaining</th>
                </>}
                {kind === 'design' && (
                  <>
                    <th className="px-3 py-2">Status / Owner</th>
                    <th className="px-3 py-2">TAT Due</th>
                  </>
                )}
                {kind === 'crm' && sub === 'design_approvals' && <th className="px-3 py-2">TAT Due</th>}
                {canAct && <th className="px-3 py-2 text-right">Action</th>}
              </tr>
            </thead>
            <tbody>
              {list.isLoading && <tr><td colSpan={20} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {list.isError && <tr><td colSpan={20} className="p-8 text-center text-red-600">Failed: {(list.error as Error).message}</td></tr>}
              {!list.isLoading && rows.length === 0 && (
                <tr><td colSpan={20} className="p-8 text-center text-slate-500">Nothing pending. </td></tr>
              )}
              {rows.map((r) => {
                const overdue = !!r.promiseDate && new Date(r.promiseDate) < new Date(new Date().toDateString())
                  && ((kind === 'crm' && sub !== 'dispatched') || kind === 'dispatch');
                return (
                <tr key={r.poId} className={`border-t border-slate-100 ${overdue ? 'bg-red-200 hover:bg-red-300' : 'hover:bg-slate-50'}`}>
                  {kind !== 'purchase' && <td className="px-3 py-2">
                    {/* Popups rather than links: a CRM cannot route to
                        /purchase-orders or /products at all (CRM_ALLOWED_PATHS
                        in App.tsx), and even for roles that can, leaving a
                        half-worked queue to read one field is the wrong trade. */}
                    <PeekLink className="font-medium"
                              title="View this purchase order"
                              onOpen={() => setPeek({ kind: 'po', id: r.poId })}>
                      {r.poNumber || r.poId}
                    </PeekLink>
                    <div className="font-mono text-[11px] text-slate-500">{r.poId}</div>
                    {kind === 'design' && (r.artworkNo
                      ? <Link to={`/artworks?q=${encodeURIComponent(r.artworkNo)}`} className="font-mono text-[11px] text-emerald-700 hover:underline" title="Artwork No.">AW {r.artworkNo}</Link>
                      : <Link to={`/artworks?fromPo=${encodeURIComponent(r.poId)}`} className="text-[11px] text-blue-600 hover:underline">+ Book artwork no.</Link>)}
                  </td>}
                  <td className="px-3 py-2 text-xs min-w-[11rem]">{r.client}</td>
                  {(seesAllCrms || kind === 'design') && (
                    <td className="px-3 py-2 text-xs">
                      {r.crmName ?? <span className="text-amber-700">unassigned</span>}
                    </td>
                  )}
                  <td className="px-3 py-2 text-xs">
                    {r.productId
                      ? <PeekLink title="View the product specification"
                                  onOpen={() => setPeek({ kind: 'product', id: r.productId! })}>
                          {r.productName ?? r.jobName}
                        </PeekLink>
                      : <>
                          <span>{r.jobName}</span>
                          {/* Design needs the product for the board spec; say so
                              rather than leave the gap unnoticed. */}
                          {kind === 'design' && <div className="text-[10px] font-medium text-amber-700">no product linked</div>}
                        </>}
                    {/* The designer's status, on every CRM tab. */}
                    {kind === 'crm' && sub !== 'client_pending' && r.clientPendingReason && (
                      <div className="mt-0.5"><DesignStatusBadge status={r.clientPendingReason} by={r.clientPendingByName} on={r.clientPendingOn} clientWait={r.clientWait} compact /></div>
                    )}
                  </td>
                  {kind === 'design' && (
                    <td className="px-3 py-2 font-mono text-xs">{r.awMaterialNo || <span className="text-slate-400">—</span>}</td>
                  )}
                  <td className="px-3 py-2 text-xs">
                    {r.jobId
                      ? <>
                          <PeekLink className="font-mono text-[11px]"
                                    title="View the job card"
                                    onOpen={() => setPeek({ kind: 'job', id: String(r.jobId) })}>
                            {formatJc(r.jobId)}
                          </PeekLink>
                          {(r.linkedPoCount ?? 0) > 1 && (
                            <span className="ml-1 inline-block rounded bg-blue-100 px-1 text-[10px] font-semibold text-blue-700" title={`Clubbed: ${r.linkedPoCount} POs`}>
                              {r.linkedPoCount}PO
                            </span>
                          )}
                        </>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.orderType}</td>
                  <td className="px-3 py-2 text-right">
                    {(() => {
                      const poQty = Number(r.qty) || 0;
                      const jc = r.jcQty;
                      // A card over several POs is measured against all of them -
                      // its qty is theirs together, not this PO's alone.
                      const pos = r.linkedPoCount ?? 0;
                      const allQty = Number(r.linkedPoQty) || 0;
                      if (pos > 1 && allQty > 0 && jc != null && jc > 0) return <>
                        {jc < allQty && <span className="inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold mb-0.5">Partial</span>}
                        <div className="font-semibold">{jc.toLocaleString()}</div>
                        {kind !== 'crm' && <div className="text-[10px] text-slate-400" title={`This PO: ${poQty.toLocaleString()}`}>of {allQty.toLocaleString()} · {pos} POs</div>}
                      </>;
                      const isPartial = jc != null && jc > 0 && poQty > 0 && jc < poQty;
                      if (isPartial) return <>
                        <span className="inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold mb-0.5">Partial</span>
                        <div className="font-semibold">{jc.toLocaleString()}</div>
                        {kind !== 'crm' && <div className="text-[10px] text-slate-400">of {poQty.toLocaleString()}</div>}
                      </>;
                      // The CRM queue shows the job card's quantity once there is one.
                      if (kind === 'crm' && jc != null && jc > 0) return <span className="font-semibold">{jc.toLocaleString()}</span>;
                      return r.qty;
                    })()}
                  </td>
                  {kind === 'purchase' && <td className="px-3 py-2 text-right text-xs">
                    {r.sheetsWithWastage ?? '—'}
                    {r.sheetsWithWastage && (r.linkedPoCount ?? 0) > 1 && (
                      <div className="text-[10px] text-slate-400 whitespace-nowrap">whole card · {r.linkedPoCount} POs</div>
                    )}
                    {!!Number(r.boardBeforeJc) && (r.linkedPoCount ?? 0) > 1 && (r.boardStatus === 'Available' || r.boardStatus === 'Expected') && (
                      <div className="mt-0.5 max-w-[150px] whitespace-normal text-left text-[10px] font-medium leading-tight text-amber-700"
                           title="The board was marked for this PO alone, before the job card joined it with other POs.">
                        Board marked {r.boardMarkedOn ? fmtDate(r.boardMarkedOn) : ''} for this PO only, before the card - check it covers these sheets
                      </div>
                    )}
                  </td>}

                  {kind === 'crm' && <>
                    <td className="px-3 py-2 text-xs">
                      {r.jcDate ? fmtDate(r.jcDate) : <span className="text-slate-400" title="No job card yet: the PO date">{fmtDate(r.poDate)} (PO)</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {(() => {
                        // Since the job card was raised; since the PO before that.
                        const from = r.jcDate ?? r.poDate;
                        if (!from) return '—';
                        const ageDays = Math.floor((new Date().getTime() - new Date(from).getTime()) / 86400000);
                        return ageDays > 0 ? `${ageDays}d` : 'Today';
                      })()}
                    </td>
                    {(sub === 'promised' || sub === 'revised' || sub === 'dispatched' || sub === 'fg') && (
                      <td className="px-3 py-2 text-xs font-semibold text-brand">
                        {fmtDate(r.promiseDate)}
                        {!!r.clientSchedulePending && <div className="text-[10px] font-normal text-amber-600">Client schedule pending</div>}
                        {r.jcPromiseDate && <div className="text-[10px] font-normal text-blue-600">batch</div>}
                        {!!Number(r.needsBatchDod) && (
                          <div className="mt-0.5 w-fit rounded bg-red-100 px-1 text-[10px] font-semibold text-red-700"
                               title="An earlier job card of this PO has been dispatched. This job card is the balance: give it its own DOD.">
                            Balance — set batch DOD
                          </div>
                        )}
                      </td>
                    )}
                    {(sub === 'promised' || sub === 'revised') && <>
                      <td className="px-3 py-2 text-xs text-red-600 line-through">{r.oldPromiseDate ? fmtDate(r.oldPromiseDate) : ''}</td>
                      <td className="px-3 py-2 text-xs">{r.revisionReason || ''}</td>
                    </>}
                    {sub === 'dispatched' && <>
                      <td className="px-3 py-2 text-xs">{r.invoiceNo}</td>
                      <td className="px-3 py-2 text-xs">{fmtDate(r.dispatchDate ?? null)}</td>
                    </>}
                    {sub === 'fg' && (() => {
                      const poQty = Number(r.totalBoxes) || 0;
                      const produced = Number(r.producedQty) || 0;
                      const dispatched = Number(r.dispatchedQty) || 0;
                      const scrapped = Number(r.scrappedQty) || 0;
                      const remaining = produced - dispatched - scrapped;
                      const isShort = produced < poQty;
                      const isApproved = !!r.dispatchApproved;
                      return <>
                        <td className="px-3 py-2 text-xs text-right">{poQty || r.qty}</td>
                        <td className="px-3 py-2 text-xs text-right font-semibold">{produced}</td>
                        <td className="px-3 py-2 text-xs text-right">{dispatched}</td>
                        <td className="px-3 py-2 text-xs text-right">{scrapped || '—'}</td>
                        <td className="px-3 py-2 text-xs text-right font-semibold">{remaining}</td>
                        <td className="px-3 py-2 text-xs">
                          {r.pendingRequest && <span className="inline-block px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-semibold mr-1">{r.pendingRequest} Req</span>}
                          {isShort && <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold mr-1">Short</span>}
                          {isApproved
                            ? <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold">Approved</span>
                            : <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold">Pending</span>}
                        </td>
                      </>;
                    })()}
                    {sub !== 'fg' && <td className="px-3 py-2 text-xs">
                      {r.boardStatus === 'Available' ? <span className="text-emerald-700">Available</span>
                        : <span className="text-amber-700">{(!r.boardStatus || r.boardStatus === 'Not Ready') ? 'No Status' : r.boardStatus}{r.boardExpectedDate ? ` - ${fmtDate(r.boardExpectedDate)}` : ''}</span>}
                    </td>}
                    <td className="px-3 py-2">
                      <ProgressCell p={progressOf.get(r.poId)} name={r.poNumber || r.poId} loading={progress.isLoading}
                                    onApprove={canAct ? (id) => run(id, 'design-action', { poId: id, action: 'receive_approval' }) : undefined}
                                    approving={busy === r.poId} />
                      <MasterFileBadge row={r} />
                    </td>
                  </>}
                  {kind === 'purchase' && <>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {r.promiseDate ? <span className="font-semibold text-brand">{fmtDate(r.promiseDate)}</span> : <span className="text-amber-700">not set</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">{formatInches(r.cutL, r.cutW)}</td>
                    <td className="px-3 py-2 text-xs">{formatInches(r.fullL, r.fullW)}</td>
                    <td className="px-3 py-2 text-xs">{r.gsm}</td>
                    <td className="px-3 py-2 text-xs">{r.board}</td>
                    <td className="px-3 py-2 text-xs">{r.partyGsm ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">{r.topFinish ?? '—'}</td>
                    <td className="px-3 py-2 text-xs"
                        title={r.boardSpecPo ? `Filled with the artwork for PO ${r.boardSpecPo}` : r.boardSpecByName ? 'Filled in Product Master' : ''}>
                      {r.boardSpecByName
                        ? <>{r.boardSpecByName}<span className="block text-[10px] text-slate-500">{fmtDateTime(r.boardSpecOn)}</span></>
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      <PurchaseTatCell row={r} />
                    </td>
                  </>}
                  {kind === 'jobcard' && <>
                    <td className="px-3 py-2 text-xs">
                      {r.promiseDate ? fmtDate(r.promiseDate)
                        : <span className="text-amber-700">not set</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.boardStatus === 'Available'
                        ? <span className="text-emerald-700">Available</span>
                        : <span className="text-amber-700">{(!r.boardStatus || r.boardStatus === 'Not Ready') ? 'No Status' : r.boardStatus}{r.boardExpectedDate ? ` · ${fmtDate(r.boardExpectedDate)}` : ''}</span>}
                    </td>
                  </>}
                  {kind === 'dispatch' && <>
                    <td className="px-3 py-2 text-xs">{fmtDate(r.promiseDate)}</td>
                    <td className="px-3 py-2 text-xs text-right font-semibold">
                      {dispatchRemaining(r) ?? '—'}
                    </td>
                  </>}

                  {kind === 'design' && (
                    <td className="px-3 py-2 text-xs">
                      {r.designStatus ? (
                        <>
                          <div className="font-semibold text-amber-700">
                            {r.designStatus === 'Claimed' ? 'In Design'
                              : r.designStatus === 'ApprovalSent' ? 'Artwork Sent'
                              : r.designStatus === 'ApprovalReceived' ? 'Awaiting Approval'
                              : r.designStatus === 'Done' ? 'Job Processed'
                              : r.designStatus}
                          </div>
                          {r.designDoneByName && <div className="text-[10px] text-slate-500">{r.designDoneByName}</div>}
                          {r.clientPendingReason && <div className="mt-0.5 text-[10px] font-semibold text-amber-700">{r.clientPendingReason}</div>}
                        </>
                      ) : (
                        <span className="text-slate-400">Unclaimed</span>
                      )}
                    </td>
                  )}

                  {kind === 'design' && (
                    <td className="px-3 py-2 text-xs">
                      <DesignTatCell row={r} onRun={run} busy={busy === r.poId} />
                    </td>
                  )}
                  {kind === 'crm' && sub === 'design_approvals' && (
                    <td className="px-3 py-2 text-xs">
                      <DesignTatCell row={r} onRun={run} busy={busy === r.poId} />
                    </td>
                  )}
                  {canAct && (
                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                      <RowAction kind={kind} row={r} sub={sub} busy={busy === r.poId} onRun={run} />
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-sm">
            <span className="text-slate-500">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-2">
              <button type="button" className="btn-ghost text-xs py-1"
                      disabled={page <= 1 || list.isFetching}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}>
                ← Previous
              </button>
              <button type="button" className="btn-ghost text-xs py-1"
                      disabled={page >= totalPages || list.isFetching}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {peek && <RecordPeek target={peek} onClose={() => setPeek(null)} />}
    </div>
  );
}

/**
 * Live countdown for the TAT cell.  Renders "3h 47m left" (amber) while
 * we're still within the SLA, or "Overdue 2h 5m" (red) once the deadline
 * has passed.  Re-ticks every 30 seconds so the office sees the clock
 * change without a page reload.
 */
function Countdown({ dueDate }: { dueDate: Date }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  const diffMs = dueDate.getTime() - now.getTime();
  const overdue = diffMs < 0;
  const absSec = Math.floor(Math.abs(diffMs) / 1000);
  const d = Math.floor(absSec / 86400);
  const h = Math.floor((absSec % 86400) / 3600);
  const m = Math.floor((absSec % 3600) / 60);
  const parts = [d && `${d}d`, (d || h) && `${h}h`, `${m}m`].filter(Boolean).join(' ');
  const label = overdue ? `Overdue ${parts}` : `${parts} left`;
  const dueLabel = dueDate.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  return (
    <div className={`text-[10px] leading-tight ${overdue ? 'text-red-700 font-semibold' : 'text-amber-700'}`}>
      <div>{label}</div>
      <div className="text-slate-400 font-normal">by {dueLabel}</div>
    </div>
  );
}

function PurchaseTatCell({ row }: { row: Row }) {
  const openSla = useOpenSlaInstance('PO', row.poId, !row.boardStatus || row.boardStatus !== 'Available', 'PURCHASE_TAT');

  if (row.boardStatus === 'Available') {
    return <span className="text-emerald-700 font-semibold text-xs">Available</span>;
  }

  let calculatedDue: Date | null | undefined;
  if (openSla.data && openSla.data.deadlineAt) {
    if (openSla.data.pausedAt) {
      return (
        <div className="flex flex-col gap-0.5 text-xs">
          <span className="text-amber-700 font-medium">Paused</span>
          {row.boardExpectedDate && <span className="text-[10px] text-slate-500">Exp: {fmtDate(row.boardExpectedDate)}</span>}
        </div>
      );
    }
    calculatedDue = new Date(openSla.data.deadlineAt);
  } else if (row.boardStatus === 'Expected') {
    return (
      <div className="flex flex-col gap-0.5 text-xs">
        <span className="text-amber-700 font-medium">Expected</span>
        {row.boardExpectedDate && <span className="text-[10px] text-slate-500">{fmtDate(row.boardExpectedDate)}</span>}
      </div>
    );
  } else if (row.orderType === 'Repeat' && row.poDate) {
    calculatedDue = calculateDueDate(row.poDate, 8, 'business');
  } else if (row.orderType !== 'Repeat' && row.designStatus === 'ApprovalSent' && row.designUpdatedOn) {
    calculatedDue = calculateDueDate(row.designUpdatedOn, 8, 'business');
  }

  if (calculatedDue) {
    return (
      <div className="flex flex-col gap-1">
        <Countdown dueDate={calculatedDue} />
      </div>
    );
  }

  return <span className="text-slate-400 text-xs">—</span>;
}

function DesignTatCell({ row, busy, onRun }: { row: Row; busy: boolean; onRun: (id: string, path: string, body: unknown) => void }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.rollId === ROLE.SuperAdmin;
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('18:00');

  // Prefer the server-side SLA instance if there's an open one — that
  // way pauses, overrides, and edited rule hours all show up correctly.
  // Fall back to the local calculation (TAT settings, then baked-in
  // defaults) for the transitional period while Phase 0/1 rolls out.
  // The design clock only — not the purchase one on the same order.
  const openSla = useOpenSlaInstance('PO', row.poId, !!row.designStatus && row.designStatus !== 'Done', 'DESIGN_TAT');
  const tatSettings = useQuery<TatSettings>({
    queryKey: ['settings-tat'],
    queryFn: () => api<TatSettings>('/api/settings/tat'),
    staleTime: 5 * 60_000,
    enabled: !openSla.data,
  });
  const tat = tatSettings.data ?? {
    claimedHours: 4, claimedMode: 'business' as const,
    approvalSentHours: 48, approvalSentMode: 'calendar' as const,
    approvalReceivedHours: 6, approvalReceivedMode: 'business' as const,
  };

  if (!row.designStatus || row.designStatus === 'Done' || !row.designUpdatedOn) {
    return null;
  }

  let calculatedDue: Date | null | undefined;
  if (openSla.data?.pausedAt) {
    // Stopped while the design status waits on the client.
    return (
      <div className="flex flex-col items-start gap-0.5 text-[10px] leading-tight">
        <span className="font-semibold text-amber-700">⏸ Clock stopped</span>
        <span className="text-slate-500">waiting on client</span>
      </div>
    );
  }
  if (openSla.data) {
    calculatedDue = new Date(openSla.data.deadlineAt);
  } else if (row.designTatOverride) {
    const rawDate = row.designTatOverride.replace('[TATOverride] ', '');
    calculatedDue = new Date(rawDate);
  } else if (row.designStatus === 'Claimed') {
    calculatedDue = calculateDueDate(row.designUpdatedOn, tat.claimedHours, tat.claimedMode);
  } else if (row.designStatus === 'ApprovalSent') {
    calculatedDue = calculateDueDate(row.designUpdatedOn, tat.approvalSentHours, tat.approvalSentMode);
  } else if (row.designStatus === 'ApprovalReceived') {
    calculatedDue = calculateDueDate(row.designUpdatedOn, tat.approvalReceivedHours, tat.approvalReceivedMode);
  }

  return (
    <div className="relative flex flex-col items-start gap-1">
      {calculatedDue && <Countdown dueDate={calculatedDue} />}
      {row.designMessage && row.designStatus === 'ApprovalSent' && (
        <div className="mt-1 inline-flex items-start gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900 max-w-xs break-words"
             title={row.designMessage.replace('[DesignMessage] ', '')}>
          <span aria-hidden>💬</span>
          <span>{row.designMessage.replace('[DesignMessage] ', '')}</span>
        </div>
      )}
      {isSuperAdmin && (
        <button className="text-[10px] text-blue-600 hover:underline" onClick={() => setOpen(true)}>
          Edit TAT
        </button>
      )}
      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20" />
          <div className="absolute left-0 top-full mt-1 z-50 flex flex-col gap-2 border border-slate-200 rounded-md p-3 bg-white shadow-xl text-xs w-64">
            <div className="font-semibold text-slate-800 mb-1">Override TAT Due Date</div>
            <div className="flex gap-2">
              <input type="date" className="input py-1 text-xs flex-1" value={date} onChange={(e) => setDate(e.target.value)} />
              <input type="time" className="input py-1 text-xs w-24" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button className="btn-ghost text-xs py-1" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn-primary text-xs py-1" disabled={!date || !time || busy}
                      onClick={() => {
                        const isoString = new Date(`${date}T${time}:00`).toISOString();
                        onRun(row.poId, 'design-action', { poId: row.poId, action: 'override_tat', overrideDate: isoString });
                        setOpen(false);
                      }}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Super Admin escape hatch on a design row: pick another designer
 * from the eligible pool or unassign entirely.  Auto-assign is the
 * normal flow; this exists for the day something goes sideways.
 */
function ReassignPopover({ row, busy, onRun }: {
  row: Row; busy: boolean;
  onRun: (id: string, path: string, body: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const [toUserId, setToUserId] = useState<string>('');
  const [reason, setReason] = useState('');
  const designers = useQuery({
    enabled: open,
    queryKey: ['designers'],
    queryFn: async () => {
      const users = await api<{ userId: number; name: string; rollId: number | null; isActive: boolean }[]>('/api/users');
      return users.filter((u) => u.rollId === ROLE.Designers && u.isActive)
        .map((u) => ({ id: u.userId, name: u.name }));
    },
    staleTime: 5 * 60_000,
  });
  return (
    <div className="relative">
      <button className="text-[10px] text-blue-600 hover:underline mt-1"
              disabled={busy} onClick={() => setOpen(true)}>
        Reassign
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20" />
          <div className="absolute right-0 top-full mt-1 z-50 flex flex-col gap-2 border border-slate-200 rounded-md p-3 bg-white shadow-xl text-xs w-64">
            <div className="font-semibold text-slate-800 mb-1">Reassign design</div>
            <label className="text-slate-500">To designer</label>
            <select className="input py-1 text-xs" value={toUserId}
                    onChange={(e) => setToUserId(e.target.value)}>
              <option value="">— Unassign —</option>
              {designers.data?.map((d) => (
                <option key={d.id} value={String(d.id)}>{d.name}</option>
              ))}
            </select>
            <label className="text-slate-500">Reason</label>
            <input type="text" className="input py-1 text-xs"
                   placeholder="e.g. workload rebalance"
                   value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="flex justify-end gap-2 mt-2">
              <button className="btn-ghost text-xs py-1" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn-primary text-xs py-1" disabled={busy || !reason.trim()}
                      onClick={() => {
                        onRun(row.poId, 'design-action', {
                          poId: row.poId, action: 'reassign',
                          toUserId: toUserId ? Number(toUserId) : null,
                          reason: reason.trim(),
                        });
                        setOpen(false);
                      }}>
                {busy ? 'Saving…' : 'Reassign'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Small "History" link on the design queue row.  Opens a modal with the
 * PO's full event timeline — created, claimed, artwork sent, approval
 * received, job processed, etc.  Read-only.  The API returns every row
 * from tblEventLog with EntityType='PO' + EntityId=<poId> sorted
 * chronologically, so this UI just formats and labels.
 */
function TimelineButton({ poId, poNumber }: { poId: string; poNumber: string | null }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    enabled: open,
    queryKey: ['flow-timeline', poId],
    queryFn: () => api<{
      events: { eventCode: string; occurredAt: string; reason: string | null;
                actorUserId: number | null; actorName: string | null }[];
    }>(`/api/flow/timeline/${encodeURIComponent(poId)}`),
    staleTime: 30_000,
  });
  return (
    <>
      <button className="text-[10px] text-blue-600 hover:underline" onClick={() => setOpen(true)}>
        History
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
          <div className="bg-white rounded-md shadow-xl border border-slate-200 p-4 w-full max-w-md text-sm max-h-[80vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="font-semibold text-slate-800 mb-3 text-base">
              History — {poNumber || poId}
            </div>
            {q.isLoading && <div className="text-slate-500 text-xs">Loading…</div>}
            {q.isError && <div className="text-red-600 text-xs">Failed to load.</div>}
            {q.data && q.data.events.length === 0 && (
              <div className="text-slate-500 text-xs">No events recorded yet.</div>
            )}
            {q.data && q.data.events.length > 0 && (
              <ol className="space-y-2">
                {q.data.events.map((e, i) => (
                  <li key={i} className="flex flex-col border-l-2 border-blue-300 pl-2">
                    <div className="text-xs font-semibold text-slate-800">{eventLabel(e.eventCode)}</div>
                    <div className="text-[11px] text-slate-500">
                      {formatEventTime(e.occurredAt)}
                      {e.actorName && <> · <span className="text-slate-700">{e.actorName}</span></>}
                    </div>
                    {e.reason && (
                      <div className="text-[11px] text-slate-600 italic mt-0.5">"{e.reason}"</div>
                    )}
                  </li>
                ))}
              </ol>
            )}
            <div className="flex justify-end mt-4">
              <button className="btn-ghost text-xs py-1" onClick={() => setOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const EVENT_LABELS: Record<string, string> = {
  PO_CREATED: 'Order received',
  PO_EDITED: 'Order edited',
  PO_CANCELLED: 'Order cancelled',
  DESIGN_CLAIMED: 'Designer claimed',
  DESIGN_UNCLAIMED: 'Designer released',
  ARTWORK_SENT: 'Artwork sent for approval',
  ARTWORK_APPROVAL_RECEIVED: 'Client approval received',
  DESIGN_DONE: 'Job processed',
  DESIGN_TAT_OVERRIDDEN: 'TAT overridden',
  CLIENT_PENDING_SET: 'Design status',
  CLIENT_PENDING_CLEARED: 'Design status cleared',
  BOARD_MARKED_AVAILABLE: 'Board marked available',
  BOARD_MARKED_EXPECTED: 'Board expected date set',
  JOB_CARD_CREATED: 'Job card created',
  JOB_CARD_CLOSED: 'Job card closed',
  DOD_SET: 'DOD set',
  DOD_REVISED: 'DOD revised',
  DISPATCH_RECORDED: 'Dispatched',
  DISPATCH_CANCELLED: 'Dispatch cancelled',
};

function eventLabel(code: string): string {
  return EVENT_LABELS[code] ?? code.replace(/_/g, ' ').toLowerCase();
}

function formatEventTime(s: string): string {
  const d = new Date(s.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function RowAction({ kind, row, busy, onRun, sub }: {
  kind: QueueKind; row: Row; busy: boolean;
  onRun: (id: string, path: string, body: unknown) => void;
  sub?: string;
}) {
  const { user } = useAuth();
  const isSuperAdmin = user?.rollId === ROLE.SuperAdmin;
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [open, setOpen] = useState(false);

  // The board spec a designer sends with the artwork, started from what the
  // product already has; the API saves it to Product Master with their name.
  const [spec, setSpec] = useState({ cutL: '', cutW: '', gsm: '', paperType: '', topFinish: '' });
  // The Artwork Register entry booked against this PO, when there is one.
  const [aw, setAw] = useState<{ artworkNo: string; sheetSize: string | null; cartonSize: string | null; ups: string | null; dieNo: string | null; cartonType: string | null } | null>(null);
  const specLists = useQuery({
    enabled: kind === 'design' && open,
    queryKey: ['board-spec-lists'],
    queryFn: async () => {
      const [papers, finishes] = await Promise.all([
        api<{ id: string; name: string }[]>('/api/lookups/paper-types'),
        api<{ id: string; name: string }[]>('/api/lookups/top-finishes'),
      ]);
      return { papers, finishes };
    },
    staleTime: 10 * 60_000,
  });
  const hasProduct = !!row.productId;
  // All optional: only a number that was typed has to be above 0.
  const specOk = !hasProduct || [spec.cutL, spec.cutW, spec.gsm].every((v) => v.trim() === '' || Number(v) > 0);
  const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));
  const setSpecField = (k: keyof typeof spec) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setSpec((s) => ({ ...s, [k]: e.target.value }));

  if (kind === 'crm') {
    if (sub === 'client_pending') {
      return <DesignStatusBadge status={row.clientPendingReason} by={row.clientPendingByName} on={row.clientPendingOn} clientWait={row.clientWait} />;
    }
    if (sub === 'design_approvals') {
      return (
        <div className="flex flex-col items-end gap-1.5">
          <button className="btn-primary text-xs py-1" disabled={busy}
                  onClick={() => onRun(row.poId, 'design-action', { poId: row.poId, action: 'receive_approval' })}>
            {busy ? 'Saving…' : 'Artwork Approval Received'}
          </button>
        </div>
      );
    }
    if (row.dispatchId && sub !== 'fg') {
      return (
        <div className="flex justify-end items-center gap-2">
          <span className="text-emerald-700 text-xs font-semibold">Dispatched</span>
          {isSuperAdmin && (
            <button className="text-xs text-red-600 hover:underline" disabled={busy}
                    onClick={() => { if (confirm(`Cancel dispatch ${row.dispatchId}? The job will return to pending.`)) onRun(row.poId, 'cancel-dispatch', { dispatchId: row.dispatchId }); }}>
              Cancel
            </button>
          )}
        </div>
      );
    }
    if (sub === 'fg') {
      const isApproved = !!row.dispatchApproved;
      const hasPending = !!row.pendingRequest;
      return (
        <div className="flex flex-col items-end gap-1">
          {!isApproved && !hasPending && (
            <button className="btn-primary text-xs py-1" disabled={busy}
                    onClick={() => onRun(row.poId, 'approve-dispatch', { jobId: row.jobId })}>
              {busy ? 'Saving…' : 'Approve Dispatch'}
            </button>
          )}
          {isApproved && <span className="text-emerald-700 text-[10px] font-semibold">Dispatch Approved</span>}
          {!hasPending && !open && (
            <button className="text-xs text-slate-500 hover:text-slate-700" onClick={() => setOpen(true)}>
              More actions...
            </button>
          )}
          {open && !hasPending && (
            <div className="flex flex-col gap-1 border border-slate-200 rounded p-2 bg-white text-xs w-64">
              <input className="input py-1 text-xs" placeholder="Reason..." value={reason}
                     onChange={(e) => setReason(e.target.value)} />
              <div className="flex gap-1">
                <input type="number" className="input py-1 text-xs w-20" placeholder="Scrap qty"
                       value={date} onChange={(e) => setDate(e.target.value)} min="1" />
                <button className="btn-ghost text-xs py-1" disabled={!date || !reason.trim() || busy}
                        onClick={() => onRun(row.poId, 'scrap', { jobId: row.jobId, qty: Number(date), reason })}>
                  Scrap
                </button>
              </div>
              <div className="flex gap-1">
                {isSuperAdmin && (
                  <button className="btn-ghost text-xs py-1 text-red-700 font-semibold" disabled={!reason.trim() || busy}
                          onClick={() => { if (confirm('Close this job card? It will be removed from all queues.')) onRun(row.poId, 'close-job', { jobId: row.jobId, reason }); }}>
                    Close Job
                  </button>
                )}
                <button className="btn-ghost text-xs py-1 text-blue-700" disabled={!reason.trim() || busy}
                        onClick={() => onRun(row.poId, 'request', { jobId: row.jobId, type: 'Reissue', reason })}>
                  Request Reissue
                </button>
              </div>
              <button className="text-[10px] text-slate-400 hover:text-slate-600 self-end" onClick={() => setOpen(false)}>
                cancel
              </button>
            </div>
          )}
          {hasPending && <span className="text-purple-700 text-[10px] font-semibold">{row.pendingRequest} request pending</span>}
        </div>
      );
    }
    const isRevision = row.promiseDate != null;
    const needsBatch = !!Number(row.needsBatchDod) && row.jobId != null;

    return (
      <div className="flex flex-col items-end gap-1.5">
        <button className={`text-xs py-1 ${needsBatch ? 'btn bg-red-600 text-white hover:bg-red-700' : isRevision ? 'btn-ghost' : 'btn-primary'}`} onClick={() => setOpen(true)}>
          {needsBatch ? 'Set batch DOD' : isRevision ? 'Revise DOD' : 'Set DOD'}
        </button>
        {open && <DodDialog row={row} onClose={() => setOpen(false)} />}
      </div>
    );
  }

  if (kind === 'jobcard') {
    return (
      <Link to={`/job-orders/new?po=${encodeURIComponent(row.poId)}`}
            className="btn-primary text-xs py-1 inline-block">
        Create job card
      </Link>
    );
  }

  if (kind === 'design') {
    return (
      <div className="flex flex-col items-end gap-1.5">
        {row.designStatus === 'Claimed' ? (
          <>
            <button className="btn-primary text-xs py-1"
                    onClick={() => {
                      setSpec({
                        cutL: row.cutL ?? '', cutW: row.cutW ?? '', gsm: row.gsm ?? '',
                        paperType: row.paperTypeId ?? '', topFinish: row.topFinishId ?? '',
                      });
                      setAw(null);
                      setOpen(true);
                      // What the designer booked in the Artwork Register for this PO:
                      // its sheet cut size fills the board cut size.
                      if (row.artworkNo) {
                        void api<{ rows: { artworkNo: string; sheetSize: string | null; cartonSize: string | null; ups: string | null; dieNo: string | null; cartonType: string | null }[] }>(
                          `/api/artworks?q=${encodeURIComponent(row.artworkNo)}`)
                          .then((r) => {
                            const hit = r.rows.find((x) => x.artworkNo === row.artworkNo);
                            if (!hit) return;
                            setAw(hit);
                            const m = /([\d.]+)\s*[xX×*]\s*([\d.]+)/.exec(hit.sheetSize ?? '');
                            if (m) setSpec((x) => ({ ...x, cutL: m[1]!, cutW: m[2]! }));
                          })
                          .catch(() => undefined);
                      }
                    }}>
              Artwork Approval Sent
            </button>
            {open && (
              // Modal, not popover.  The row's card has overflow-hidden,
              // so an absolute-positioned popover gets clipped and shoves
              // the row height (see the DOD modal below — same fix).
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
                {/* text-left / whitespace-normal: the row's action cell is
                    right-aligned and no-wrap, and the modal inherits both. */}
                <div className="bg-white rounded-md shadow-xl border border-slate-200 p-4 w-full max-w-md text-sm text-left whitespace-normal"
                     onClick={(e) => e.stopPropagation()}>
                  <div className="font-semibold text-slate-800 mb-3 text-base">
                    Send for Approval — {row.poNumber || row.poId}
                  </div>
                  {hasProduct ? (
                    <div className="mb-3 rounded border border-slate-200 bg-slate-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">Board for Purchase</div>
                      {aw && (
                        <p className="mb-2 rounded bg-emerald-50 px-2 py-1 text-[11px] text-emerald-800">
                          From Artwork Register {aw.artworkNo}:
                          {aw.sheetSize && <> cut size {aw.sheetSize}</>}
                          {aw.cartonSize && <> · carton {aw.cartonSize}</>}
                          {aw.ups && <> · {aw.ups} ups</>}
                          {aw.dieNo && <> · die {aw.dieNo}</>}
                          {aw.cartonType && <> · {aw.cartonType}</>}
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex flex-col gap-0.5 text-xs text-slate-500">Cut size length (mm)
                          <input type="number" min={1} step="any" className="input py-1" value={spec.cutL} onChange={setSpecField('cutL')} />
                        </label>
                        <label className="flex flex-col gap-0.5 text-xs text-slate-500">Cut size width (mm)
                          <input type="number" min={1} step="any" className="input py-1" value={spec.cutW} onChange={setSpecField('cutW')} />
                        </label>
                        <label className="flex flex-col gap-0.5 text-xs text-slate-500">GSM
                          <input type="number" min={1} step="any" className="input py-1" value={spec.gsm} onChange={setSpecField('gsm')} />
                        </label>
                        <label className="flex flex-col gap-0.5 text-xs text-slate-500">Paper type
                          <select className="input py-1" value={spec.paperType} onChange={setSpecField('paperType')}>
                            <option value="">{specLists.isLoading ? 'Loading…' : 'Select…'}</option>
                            {(specLists.data?.papers ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        </label>
                        <label className="col-span-2 flex flex-col gap-0.5 text-xs text-slate-500">Top finish
                          <select className="input py-1" value={spec.topFinish} onChange={setSpecField('topFinish')}>
                            <option value="">Select…</option>
                            {(specLists.data?.finishes ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        </label>
                      </div>
                      {Number(spec.cutL) > 0 && Number(spec.cutW) > 0 && (
                        <p className="text-[11px] text-slate-500 mt-1.5">
                          = {(Number(spec.cutL) / 25.4).toFixed(1)}" x {(Number(spec.cutW) / 25.4).toFixed(1)}" for the board supplier
                        </p>
                      )}
                      <p className="text-[11px] text-slate-500 mt-1">
                        Optional — fill what you know; a blank keeps what the product already has.
                        Saved to the product in Product Master with your name, so Purchase can order the board.
                        {row.boardSpecByName && <> Last filled by {row.boardSpecByName}{row.boardSpecOn ? `, ${fmtDateTime(row.boardSpecOn)}` : ''}.</>}
                      </p>
                    </div>
                  ) : (
                    <p className="mb-3 text-[11px] text-amber-700">
                      This order has no product linked, so the board details can't be saved here.
                    </p>
                  )}
                  <div className="flex flex-col gap-1 mb-3">
                    <label className="text-slate-500 text-xs">Message for CRM (optional)</label>
                    <textarea className="input py-1.5 text-sm resize-none" rows={3} placeholder="e.g. Please confirm the logo size..." value={reason} onChange={(e) => setReason(e.target.value)} />
                  </div>
                  <div className="flex justify-end items-center gap-2 mt-2">
                    {!specOk && <span className="text-[11px] text-amber-700 mr-auto">Cut size and GSM must be more than 0.</span>}
                    <button className="btn-ghost text-xs py-1" onClick={() => setOpen(false)}>Cancel</button>
                    <button className="btn-primary text-xs py-1" disabled={busy || !specOk}
                            onClick={() => {
                              onRun(row.poId, 'design-action', {
                                poId: row.poId, action: 'send_approval', message: reason,
                                ...(hasProduct ? {
                                  cutL: numOrNull(spec.cutL), cutW: numOrNull(spec.cutW), gsm: numOrNull(spec.gsm),
                                  paperType: spec.paperType || null, topFinish: spec.topFinish || null,
                                } : {}),
                              });
                              setOpen(false);
                            }}>
                      {busy ? 'Saving…' : 'Send'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : row.designStatus === 'ApprovalSent' ? (
          // The CRM's design-approvals subtab has the same button — kept
          // there for the CRM flow.  Duplicated here because the client
          // sometimes reaches the designer directly (email/WhatsApp) and
          // the designer records the approval without a CRM hop.
          <>
            <span className="text-[11px] text-slate-500">Awaiting client…</span>
            <button className="btn-primary text-xs py-1" disabled={busy}
                    onClick={() => onRun(row.poId, 'design-action', { poId: row.poId, action: 'receive_approval' })}>
              {busy ? 'Saving…' : 'Approval Received'}
            </button>
          </>
        ) : row.designStatus === 'ApprovalReceived' ? (
          <button className="btn-primary text-xs py-1" disabled={busy}
                  onClick={() => onRun(row.poId, 'design-action', { poId: row.poId, action: 'done' })}>
            {busy ? 'Saving…' : 'Mark Job Processed'}
          </button>
        ) : row.designStatus === 'Done' ? (
          // History rows only.  No verb — the design is done; keeping
          // the badge helps a designer spot their own past work.
          <span className="text-xs font-semibold text-emerald-700">Job Processed</span>
        ) : (
          // Nobody holds it: a designer (or Super Admin) claims it.  The API
          // refuses if another designer got there first.
          <button className="btn-primary text-xs py-1" disabled={busy}
                  onClick={() => onRun(row.poId, 'design-action', { poId: row.poId, action: 'claim' })}>
            {busy ? 'Claiming…' : 'Claim'}
          </button>
        )}

        {row.designStatus !== 'Done' && <DesignStatusSelect poId={row.poId} status={row.clientPendingReason} />}
        <TimelineButton poId={row.poId} poNumber={row.poNumber} />

        {isSuperAdmin && row.designStatus && row.designStatus !== 'Done' && (
          <ReassignPopover row={row} busy={busy} onRun={onRun} />
        )}
      </div>
    );
  }

  if (kind === 'purchase') {
    if (row.boardStatus === 'Available') {
      return (
        <div className="flex flex-col items-end gap-1">
          <span className="text-emerald-700 text-xs font-semibold">Available</span>
          {isSuperAdmin && (
            <button className="text-xs text-red-600 hover:underline" disabled={busy} 
                    onClick={() => onRun(row.poId, 'board', { poId: row.poId, status: 'Not Ready' })}>
              Revert to No Status
            </button>
          )}
        </div>
      );
    }
    if (row.boardStatus === 'Expected') {
      return (
        <div className="flex flex-col justify-end items-end gap-1">
          <span className="text-xs text-amber-700 font-medium">{fmtDate(row.boardExpectedDate)}</span>
          <button className="btn-primary text-xs py-1" disabled={busy}
                  onClick={() => onRun(row.poId, 'board', { poId: row.poId, status: 'Available' })}>
            {busy ? 'Saving…' : 'Available'}
          </button>
        </div>
      );
    }
    return (
      <div className="flex justify-end items-center gap-2">
        <input type="date" className="input py-1 text-xs w-36" value={date}
               onChange={(e) => setDate(e.target.value)} title="Expected available date" />
        <button className="btn-ghost text-xs py-1" disabled={!date || busy}
                onClick={() => onRun(row.poId, 'board', { poId: row.poId, status: 'Expected', expectedDate: date })}>
          Expected
        </button>
        <button className="btn-primary text-xs py-1" disabled={busy}
                onClick={() => onRun(row.poId, 'board', { poId: row.poId, status: 'Available' })}>
          {busy ? 'Saving…' : 'Available'}
        </button>
      </div>
    );
  }

  // dispatch
  return (
    <>
      <button className="btn-primary text-xs py-1" disabled={busy} onClick={() => setOpen(true)}>Record dispatch</button>
      {open && <DispatchDialog row={row} onClose={() => setOpen(false)} />}
    </>
  );
}

export interface PoProgress {
  poId: string; at: number; headline: string;
  steps: { key: string; label: string; done: boolean; text: string; action?: 'receive_approval'; waiting?: boolean }[];
}

/**
 * The order's journey as a strip of steps - green done, amber next - with
 * where it is now in words.  Click for every step.
 */
export function ProgressCell({ p, name, loading, onApprove, approving }: {
  p?: PoProgress; name: string; loading: boolean;
  /** Record that the client approved the artwork (shown on the Design step when it is with the client). */
  onApprove?: (poId: string) => void;
  approving?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!p) return <span className="text-xs text-slate-300">{loading ? '…' : '—'}</span>;
  const waiting = p.steps.some((s) => s.waiting);
  const withClient = p.steps.some((s) => s.action === 'receive_approval');
  return (
    <>
      <button type="button" className="group min-w-[11rem] text-left" onClick={() => setOpen(true)}
              title={p.steps.map((s) => `${s.done ? '✓' : '·'} ${s.label}: ${s.text}`).join("\n")}>
        <div className="flex gap-0.5">
          {p.steps.map((s, i) => (
            <span key={s.key} className={`h-1.5 flex-1 rounded-full ${s.done ? 'bg-emerald-500' : i === p.at + 1 ? 'bg-amber-400' : 'bg-slate-200'}`} />
          ))}
        </div>
        <div className={`mt-1 text-[11px] leading-tight group-hover:text-brand ${waiting || withClient ? 'font-medium text-amber-700' : 'text-slate-700'}`}>{p.headline}</div>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm overflow-hidden rounded-lg bg-white text-left text-sm whitespace-normal shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between bg-brand px-4 py-3 font-semibold text-white">
              <span>Where it is — {name}</span>
              <button type="button" className="text-xl leading-none text-white/80 hover:text-white" onClick={() => setOpen(false)} aria-label="Close">×</button>
            </div>
            <ol className="p-4">
              {p.steps.map((s, i) => (
                <li key={s.key} className="flex gap-3 pb-3 last:pb-0">
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${s.done ? 'bg-emerald-500 text-white' : i === p.at + 1 ? 'bg-amber-400 text-white' : 'bg-slate-200 text-slate-500'}`}>
                    {s.done ? '✓' : i + 1}
                  </span>
                  <div>
                    <div className={`font-medium ${s.done ? 'text-slate-800' : 'text-slate-500'}`}>{s.label}</div>
                    <div className={`text-xs ${s.waiting || s.action ? 'font-medium text-amber-700' : 'text-slate-500'}`}>{s.text}</div>
                    {s.action === 'receive_approval' && onApprove && (
                      <button type="button" className="btn-primary mt-1.5 py-1 text-xs" disabled={approving}
                              onClick={() => onApprove(p.poId)}>
                        {approving ? 'Saving…' : 'Artwork approval received'}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </>
  );
}

/** yyyy-mm-dd for a day `days` from today, local time. */
function isoInDays(days: number): string {
  const d = new Date(); d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Set, revise or (for the balance job card of a part-dispatched PO) batch
 * DOD.  Shows what the order is and what the client asked for, offers
 * one-click dates, and keeps any error inside the dialog so nothing typed
 * is lost.
 */
function DodDialog({ row, onClose }: { row: Row; onClose: () => void }) {
  const qc = useQueryClient();
  const isRevision = row.promiseDate != null;
  const poQty = Number(row.qty) || 0;
  const jc = row.jcQty;
  const needsBatch = !!Number(row.needsBatchDod) && row.jobId != null;
  const isPartial = (jc != null && jc > 0 && poQty > 0 && jc < poQty && row.jobId != null) || needsBatch;
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [instruction, setInstruction] = useState('');
  const [batchDate, setBatchDate] = useState(row.jcPromiseDate?.slice(0, 10) ?? '');
  const today = isoInDays(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const done = async () => { await qc.refetchQueries({ queryKey: ['flow-queue'] }); onClose(); };
  const save = useMutation({
    mutationFn: () => api('/api/flow/promise', {
      method: 'POST',
      body: JSON.stringify({ poId: row.poId, date, reason: isRevision ? reason.trim() : undefined, instruction: isRevision ? undefined : instruction.trim() || undefined }),
    }),
    onSuccess: done,
  });
  const saveBatch = useMutation({
    mutationFn: () => api('/api/flow/jc-promise', { method: 'POST', body: JSON.stringify({ jobId: row.jobId, date: batchDate }) }),
    onSuccess: done,
  });
  const err = save.error ?? saveBatch.error;
  const canSave = !!date && date >= today && (!isRevision || !!reason.trim()) && !save.isPending;
  const title = needsBatch ? 'Batch DOD' : isRevision ? 'Revise DOD' : 'Set DOD';
  const fact = (label: string, value: React.ReactNode) => (
    <div><div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div><div className="text-slate-800">{value || '—'}</div></div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form className="w-full max-w-md overflow-hidden rounded-lg bg-white text-left text-sm whitespace-normal shadow-xl"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => { e.preventDefault(); if (!needsBatch && canSave) save.mutate(); }}>
        <div className="flex items-start justify-between bg-brand px-4 py-3 text-white">
          <div>
            <div className="font-semibold">{title} — {row.poNumber || row.poId}</div>
            <div className="text-xs text-white/80">{row.client}</div>
          </div>
          <button type="button" className="text-xl leading-none text-white/80 hover:text-white" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="space-y-4 p-4">
          <div className="grid grid-cols-2 gap-3 rounded-md bg-slate-50 p-3 text-xs">
            <div className="col-span-2">{fact('Product', row.productName ?? row.jobName)}</div>
            {fact('Qty', (jc != null && jc > 0 ? jc : poQty).toLocaleString() + (row.jobId ? ` · ${formatJc(row.jobId)}` : ''))}
            {fact('Client asked for', row.requestedDate ? fmtDate(row.requestedDate) : '')}
            {isRevision && fact('Current DOD', <span className="font-semibold text-brand">{fmtDate(row.promiseDate)}</span>)}
            {row.jcPromiseDate && fact('Batch DOD', fmtDate(row.jcPromiseDate))}
          </div>

          {needsBatch && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              An earlier job card of this PO has been dispatched. Job card {formatJc(row.jobId!)} is the balance
              ({(jc ?? 0).toLocaleString()} boxes): give it its own delivery date. The PO's DOD stays as it is.
            </p>
          )}

          {!needsBatch && <>
            <div>
              <label className="label">{isRevision ? 'New delivery date' : 'Delivery date'} *</label>
              <input type="date" className="input" min={today} value={date} autoFocus onChange={(e) => setDate(e.target.value)} />
              {date && <div className="mt-1.5 text-xs text-slate-500">{fmtDate(date)}{date < today && <span className="ml-1 text-red-600">— this is in the past</span>}</div>}
            </div>

            {isRevision ? (
              <div>
                <label className="label">Reason for the change *</label>
                <input type="text" className="input" placeholder="e.g. Awaiting client artwork"
                       value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
            ) : (
              <div>
                <label className="label">Instruction <span className="font-normal normal-case text-slate-400">(optional)</span></label>
                <input type="text" className="input" placeholder="e.g. As per sample"
                       value={instruction} onChange={(e) => setInstruction(e.target.value)} />
              </div>
            )}
          </>}

          {isPartial && (isRevision || needsBatch) && (
            <div className={needsBatch ? '' : 'border-t border-slate-100 pt-3'}>
              <label className="label text-blue-700">Batch DOD for {row.jobId ? formatJc(row.jobId) : 'this job card'}{needsBatch ? ' *' : ' (optional)'}</label>
              <div className="flex items-center gap-2">
                <input type="date" className="input" min={today} value={batchDate} onChange={(e) => setBatchDate(e.target.value)} />
                <button type="button" className="btn whitespace-nowrap bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        disabled={!batchDate || saveBatch.isPending} onClick={() => saveBatch.mutate()}>
                  {saveBatch.isPending ? 'Saving…' : row.jcPromiseDate ? 'Update batch' : 'Set batch'}
                </button>
              </div>
            </div>
          )}

          {err && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{friendly((err as Error).message)}</div>}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          {!needsBatch && (
            <button type="submit" className="btn-primary" disabled={!canSave}>
              {save.isPending ? 'Saving…' : isRevision ? 'Save new date' : 'Set delivery date'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

/**
 * One dispatch: the quantity (required) and its paperwork — invoice,
 * packages, vehicle and driver, all optional.  A job with production entries
 * goes out in parts, up to what is ready; one without them leaves the queue
 * on this dispatch.  Stays open on an error so nothing typed is lost.
 */
function DispatchDialog({ row, onClose }: { row: Row; onClose: () => void }) {
  const qc = useQueryClient();
  const free = row.dispatchGates === false;
  const remaining = dispatchRemaining(row);
  const [f, setF] = useState({
    qty: remaining != null ? String(Math.max(0, remaining)) : String(row.totalBoxes ?? row.qty ?? ''),
    invoiceNo: '', packages: '', vehicleNo: '', driverName: '', driverMobile: '',
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));
  const qty = Number(f.qty);
  // Gates off: the quantity typed goes, whatever the card says is left.
  const qtyOk = f.qty.trim() !== '' && Number.isInteger(qty) && qty > 0 && (free || remaining == null || qty <= remaining);
  const save = useMutation({
    mutationFn: () => api('/api/flow/dispatch', {
      method: 'POST',
      body: JSON.stringify({ ...f, jobId: row.jobId, qty: String(qty) }),
    }),
    onSuccess: async () => { await qc.refetchQueries({ queryKey: ['flow-queue'] }); onClose(); },
  });
  const field = (k: Exclude<keyof typeof f, 'qty'>, label: string) => (
    <div>
      <label className="label">{label}</label>
      <input className="input" value={f[k]} onChange={set(k)} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        className="w-full max-w-lg rounded bg-white shadow-lg text-left whitespace-normal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); if (qtyOk && !save.isPending) save.mutate(); }}
      >
        <div className="card-header">
          Dispatch — {row.poNumber || row.poId}{row.jobId ? ` · ${formatJc(row.jobId)}` : ''}
        </div>
        <div className="p-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label">Qty dispatched *</label>
            <input className="input" type="number" min={1} step={1} max={free ? undefined : remaining ?? undefined} autoFocus
                   value={f.qty} onChange={set('qty')} />
            <p className={`mt-0.5 text-[11px] ${remaining != null && qty > remaining ? (free ? 'text-amber-700' : 'text-red-600') : 'text-slate-500'}`}>
              {free
                ? (remaining != null
                  ? `${Math.max(0, remaining).toLocaleString()} left on the job card${qty > remaining ? ' — this sends more than that' : ''}. The job leaves the queue once it is all sent.`
                  : 'No quantity on the job card — it leaves the queue after this dispatch.')
                : remaining != null
                  ? `${Math.max(0, remaining).toLocaleString()} ready to go — the rest can go later.`
                  : 'This job leaves the queue after this dispatch.'}
            </p>
          </div>
          {field('invoiceNo', 'Invoice No')}
          {field('packages', 'Packages')}
          {field('vehicleNo', 'Vehicle No')}
          {field('driverName', 'Driver name')}
          {field('driverMobile', 'Driver mobile')}
        </div>
        {save.isError && (
          <div className="mx-4 mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {friendly((save.error as Error).message)}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 p-3">
          <span className="mr-auto text-[11px] text-slate-500">Only the quantity is required.</span>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={!qtyOk || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save dispatch'}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Turn API error codes into something an operator can act on. */
function friendly(msg: string): string {
  const dup = /^already_dispatched:(.+)$/.exec(msg);
  if (dup) return `This job was already dispatched as ${dup[1]}. It has left the queue — refresh the list.`;
  if (msg === 'forbidden' || msg === 'forbidden_role') return 'Your account does not have permission for this action.';
  if (msg === 'not_your_order') return 'That order belongs to a different CRM.';
  if (msg === 'reason_required_to_revise') return 'Changing an existing date needs a reason.';
  if (msg === 'expected_date_required') return 'Give the date the board is expected.';
  if (msg === 'pending_request_exists') return 'There is already a pending reissue request for this job.';
  if (msg === 'already_actioned') return 'This request has already been approved or rejected.';
  if (msg === 'super_admin_only') return 'Only Super Admin can perform this action.';
  if (msg === 'not_allowed') return 'Your role does not have permission for this action.';
  if (msg === 'promise_date_in_past') return 'The delivery date cannot be in the past. Check the year.';
  if (msg === 'invalid_date') return 'Invalid date — please pick a valid date.';
  if (msg === 'board_spec_invalid') return 'Cut size and GSM must be numbers more than 0 — or leave them blank.';
  if (msg === 'paper_type_not_found') return 'That paper type is no longer in the list — pick another.';
  if (msg === 'top_finish_not_found') return 'That top finish is no longer in the list — pick another.';
  if (msg === 'po_has_no_product') return 'This order has no product linked, so the board details cannot be saved.';
  if (msg === 'already_claimed') return 'Another designer has already claimed this order — refresh the list.';
  const hasJob = /^has_active_job:(.+)$/.exec(msg);
  if (hasJob) return `Cannot cancel — this PO has an active job card (${hasJob[1]}). Delete or close the job first.`;
  const hasDisp = /^has_dispatches:(.+)$/.exec(msg);
  if (hasDisp) return `Cannot cancel — this PO has dispatches recorded against job ${hasDisp[1]}.`;
  if (msg === 'qty_required') return 'Enter the quantity dispatched — a whole number more than 0.';
  const rem = /^exceeds_remaining:(\d+)$/.exec(msg);
  if (rem) return `Cannot exceed remaining quantity (${rem[1]} available).`;
  const qcRem = /^exceeds_qc_approved:(-?\d+)$/.exec(msg);
  if (qcRem) return `Only ${Math.max(0, Number(qcRem[1])).toLocaleString()} cartons are approved by Quality and not yet dispatched — dispatch at most that.`;
  return msg;
}

/** Quality's master file for a Change / Repeat order: handed over, not found, or pending. */
function MasterFileBadge({ row }: { row: Row }) {
  if (!Number(row.needsMasterFile)) return null;
  const on = row.masterFileOn
    ? (() => {
        const [d = '', t = ''] = row.masterFileOn.split(' ');
        const [, m, day] = d.split('-');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${Number(day)} ${months[Number(m) - 1]}, ${t}`;
      })()
    : '';
  const [cls, text] = !row.masterFileOn
    ? ['bg-amber-50 text-amber-700', 'Master file pending']
    : Number(row.masterFileNotFound)
      ? ['bg-slate-200 text-slate-700', 'Master file not found']
      : ['bg-emerald-50 text-emerald-700', 'Master file handed over'];
  return (
    <div className={`mt-0.5 w-fit whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}
      title={on ? `Marked by Quality on ${on}` : 'Quality has not handed the master file over yet'}>
      {text}
    </div>
  );
}

/**
 * CRM: every order in the flow, client by client - how many have a job card,
 * how many are waiting for one, and each order's details.  The API keeps a
 * CRM to their own clients.
 */
function ClientReport({ crmUserId, onOpenPo }: { crmUserId: string; onOpenPo: (poId: string) => void }) {
  type R = ClientRow & { poDispatched?: number | null };
  const q = useQuery({
    queryKey: ['crm-client-report', crmUserId],
    queryFn: () => fetchAllQueue<R>({ kind: 'all', ...(crmUserId ? { crmUserId } : {}) }),
    staleTime: 60_000,
  });
  const left = (r: R) => Math.max(0, Math.round((Number(r.qty) || 0) - (Number(r.poDispatched) || 0)));
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-2.5">
        <div className="text-sm font-semibold text-slate-800">Job cards by client</div>
        <div className="text-xs text-slate-500">Orders not yet dispatched in full. Click a client for its orders.</div>
      </div>
      {q.isLoading && <div className="p-6 text-center text-sm text-slate-500">Loading…</div>}
      {q.isError && <div className="p-6 text-center text-sm text-red-600">Could not load: {(q.error as Error).message}</div>}
      {q.data && <ClientSummary rows={q.data.rows} balanceOf={left} onOpenPo={onOpenPo} maxHeight="max-h-[60vh]" />}
    </div>
  );
}
